<script lang="ts">
    import { PREVIEW_CONTAINER_ID, SPLIT_TESTING_KEY } from '$lib/global/constants';
    import { PREVIEW_MODE } from '$lib/global/constants';

    import { browser } from '$app/environment';
    import { afterNavigate } from '$app/navigation';
    import { page } from '$app/stores';
    import Block from '$lib/components/Blocks/Block.svelte';
    import { setGlobals, getGlobals } from '$lib/global/globals';
    import { UTM_PARAM_IDS } from '$lib/global/helper/get-query-parameter';
    import { NextPageNavigator } from '$lib/global/helper/NextPageNavigator';
    import { trackPage, trackVariant } from '$lib/global/tracking';
    import psProperties from '$lib/global/tracking/v4.ps-properties';

    import { polyfillCountryFlagEmojis } from 'country-flag-emoji-polyfill';
    import debug from 'debug';
    import unescape from 'lodash/unescape';
    import { onMount, afterUpdate, beforeUpdate } from 'svelte';

    import type { Page } from '$lib/types/Page';
    import type { ComponentType } from 'svelte';

    export let pageData: Page;

    const debugLog = debug('page');
    const STkey = SPLIT_TESTING_KEY + pageData.campaignId;

    let dataToDisplay = pageData;
    let Confetti: ComponentType;

    let mounted = false;
    let prevPageId: string | null = null;
    let prevPageName: string | null = null;
    let isHomePage = true;
    let redirectTimeout;

    debugLog('-> initial dataToDisplay', dataToDisplay);

    afterNavigate(() => {
        debugLog('-> afterNavigate');

        // Scrolls to top in preview mode after page navigation
        if (PREVIEW_MODE) {
            const previewContainerElement = document.getElementById(PREVIEW_CONTAINER_ID);

            if (previewContainerElement) {
                debugLog('-> Trigger reflow of "#perspective-frame-container" element');

                previewContainerElement.style.display = 'none';
                previewContainerElement.offsetHeight; // Trigger reflow
                previewContainerElement.style.display = '';
                previewContainerElement.scrollTop = 0;
            }
        }
    });

    beforeUpdate(() => {
        debugLog('-> beforeUpdate');
        const existingGlobals = getGlobals();
        const isPageChange = pageData.pageId !== prevPageId && mounted;
        const pageParamsSlug = $page?.params?.slug;
        // Perform A/B testing if:
        // - a B-variant exists (pageData.variantPageData)
        // - the visitor is on the root funnel page (no slug)
        //  Otherwise we render the page based on the provided slug
        const shouldPerformABTest = pageData.variantPageData && !pageParamsSlug;

        if (browser) {
            // Check for active split testing
            if (shouldPerformABTest) {
                const ST = window.sessionStorage;

                // Check session storage for defined variant
                let variant = ST.getItem(STkey);

                // If not present, calculate and set a variant
                if (!variant) {
                    variant = Math.random() < 0.5 ? 'A' : 'B';
                    ST.setItem(STkey, variant);
                }

                // Populate page data
                if (variant === 'A') {
                    dataToDisplay = pageData;
                } else if (variant === 'B') {
                    dataToDisplay = pageData.variantPageData as Page;
                }
            } else {
                dataToDisplay = pageData;
            }

            // Update globals for tracking
            setGlobals({
                ...existingGlobals,
                perspectivePageSlug: dataToDisplay.pageSlug,
                perspectivePageId: dataToDisplay.pageId,
                perspectiveCompanyId: dataToDisplay.companyId,
                perspectiveCampaignId: dataToDisplay.campaignId,
                perspectiveVersionId: dataToDisplay.campaignVersionId,
                pageId: dataToDisplay.pageId,
                pageSlug: dataToDisplay.pageSlug,
                companyId: dataToDisplay.companyId,
                campaignId: dataToDisplay.campaignId,
                workspaceId: dataToDisplay.workspaceId,
                versionId: dataToDisplay.campaignVersionId,
                trackingTitles: getTrackingTitles(),
            });

            // only track when mounted; pageId changed and the page is not the home page
            // -> home page gets tracked in onMount
            if (isPageChange && !isHomePage) {
                trackPage({
                    pageName: dataToDisplay.pageName,
                    tag: dataToDisplay.tag,
                    campaignId: dataToDisplay.campaignId,
                    pageSlug: dataToDisplay.pageSlug,
                    isLastPage: dataToDisplay.isLastPage,
                    pageId: dataToDisplay.pageId,
                    fromPageId: prevPageId,
                    fromPageName: prevPageName,
                });
            }

            // Inject custom head scripts if there are any, and they're valid
            // svelte:head isn't working here, see: https://discord.com/channels/457912077277855764/1091386239124508852

            if (dataToDisplay.headScript && isPageChange && !PREVIEW_MODE) {
                const codeSnippet = unescape(dataToDisplay.headScript);

                const parser = new DOMParser();
                const parsedSnippet = parser.parseFromString(codeSnippet, 'text/html');

                const invalidElements = Array.from(parsedSnippet.body.children).filter(
                    (node) => node.tagName !== 'SCRIPT' && node.tagName !== 'STYLE',
                );

                if (invalidElements.length > 0) {
                    console.error('Invalid HTML in code snippet.');
                } else {
                    const range = document.createRange();
                    const fragment = range.createContextualFragment(codeSnippet.trim());
                    document.head.appendChild(fragment);
                }
            }

            // update prevPageId after all updates
            if (isPageChange) {
                prevPageId = pageData.pageId;
                prevPageName = pageData.pageName;
            }
        }
    });

    // Disclaimer:
    // onMount is not called on a regular page change
    // except from index page to 2nd page, because this is route change from index to [slug]
    onMount(() => {
        debugLog('-> onMount');

        polyfillCountryFlagEmojis();

        // Track initial home page view
        if (isHomePage) {
            trackPage({
                pageName: dataToDisplay.pageName,
                tag: dataToDisplay.tag,
                campaignId: dataToDisplay.campaignId,
                pageSlug: dataToDisplay.pageSlug,
                isLastPage: dataToDisplay.isLastPage,
                pageId: dataToDisplay.pageId,
                fromPageId: null,
                fromPageName: null,
            });

            // Track page variant if split testing is active
            trackPageVariant();
        }

        mounted = true;
    });

    afterUpdate(async () => {
        // this is needed to make sure the page data is updated and this component re-renders
        dataToDisplay = pageData;
        debugLog('-> afterUpdate dataToDisplay', dataToDisplay);

        if (dataToDisplay.animation === 'confetti' && !Confetti) {
            // CAUTION: dynamic import since it's not used on every page
            // -> this causes to trigger `afterUpdate` again!
            Confetti = (await import('$lib/components/Confetti/Confetti.svelte')).default;
        }

        // Automatic redirect handling
        if (redirectTimeout) {
            // Clear eventual timeout from previous page
            clearTimeout(redirectTimeout);
        }

        if (dataToDisplay.automaticRedirect?.enabled) {
            const nextPageNavigator = new NextPageNavigator(
                dataToDisplay.campaignId,
                dataToDisplay.firstResultPage,
                dataToDisplay.pageIdSlugMap,
                dataToDisplay.trackingProperties,
            );

            const { duration, linking } = dataToDisplay.automaticRedirect;

            redirectTimeout = setTimeout(() => {
                if (linking.target === 'external' && linking.url) {
                    return nextPageNavigator.goExternal(linking.url);
                }

                if (linking.target === 'next') {
                    return nextPageNavigator.go(dataToDisplay.nextPage);
                }

                // target is a pageId
                return nextPageNavigator.go(linking.target);
            }, duration * 1000);
        }
    });

    const trackPageVariant = () => {
        debugLog('-> trackPageVariant', dataToDisplay.variantPageData?.pageId);

        if (dataToDisplay.variantPageData?.pageId) {
            const variant = window.sessionStorage.getItem(STkey);

            if (!variant) return;

            const map = {
                A: 'Original',
                B: 'Variant',
            };

            const name = [map[variant as 'A' | 'B'], dataToDisplay.pageName].join(' - ');

            trackVariant(name);
        }
    };

    const getTrackingTitles = () => {
        type TrackingKey =
            | Exclude<(typeof psProperties)[keyof typeof psProperties], 'ps_embed'>
            | (typeof UTM_PARAM_IDS)[number];

        if (pageData.context.trackingVersion > 'v3') {
            const trackingTitles: Record<TrackingKey, string> = {
                ps_first_seen_at: 'First seen at',
                ps_start_slug: 'Start slug',
                ps_start_page: 'Start page',
                ps_end_slug: 'End slug',
                ps_converted_at: 'Converted at',
                ps_completed_at: 'Completed at',
                ps_closed_at: 'Closed at',
                ps_last_seen_at: 'Last seen at',
                ps_converted: 'Has converted',
                ps_completed: 'Has completed',
                ps_closed: 'Has closed',
                ps_result_page: 'Result page',
                ps_calendly_booked: 'Calendly Booked',
                ps_cookies: 'Cookies accepted',
                ps_timezone: 'Timezone',
                utm_source: 'UTM Source',
                utm_medium: 'UTM Medium',
                utm_campaign: 'UTM Campaign',
                utm_term: 'UTM Term',
                utm_content: 'UTM Content',
            };

            pageData.fields.forEach((field) => {
                if (field.interactionType === 'answers' && field.additional.answers.length > 0) {
                    // question
                    trackingTitles[field.additional.answers[0].fieldName as TrackingKey] =
                        field.title;
                } else {
                    // form
                    field.additional.form.inputs.forEach((input) => {
                        trackingTitles[input.fieldName as TrackingKey] = input.title;
                    });
                }
            });

            return trackingTitles;
        }

        return {};
    };

    $: isHomePage = pageData.isHomePage || !!pageData.variantPageData?.pageId;
</script>

<div class="fun-relative fun-z-10">
    {#key dataToDisplay.pageId}
        {#each dataToDisplay.components as component, index}
            <Block
                id={component._id}
                language={dataToDisplay.language}
                campaignId={dataToDisplay.campaignId}
                campaignVersionId={dataToDisplay.campaignVersionId}
                companyId={dataToDisplay.companyId}
                cookiesEnabled={dataToDisplay.cookiesEnabled}
                componentType={component.componentType}
                childComponents={component.components}
                content={component.content}
                theme={dataToDisplay.theme}
                nextPage={dataToDisplay.nextPage}
                currentPage={dataToDisplay.pageId}
                resultMapping={dataToDisplay.resultMapping}
                fields={dataToDisplay.fields}
                firstResultPage={dataToDisplay.firstResultPage}
                trackingProperties={dataToDisplay.trackingProperties}
                {index}
            />
        {/each}
    {/key}
</div>

{#if dataToDisplay.animation === 'confetti'}
    <svelte:component this={Confetti} />
{/if}

{#if dataToDisplay.bodyScript && !PREVIEW_MODE}
    {@html unescape(dataToDisplay.bodyScript)}
{/if}
